import { PARASCOLASTICO, SLUG, VOLUME } from 'utils/endpoint'
import { api, CAS_DOMAIN, getDiscipline, getMateria, slugify } from 'utils'
import React, { useEffect, useMemo, useState } from 'react'
import { Image } from 'react-components/atoms'
import Breadcrumbs from 'components/atomic/Breadcrumbs'
import Error from 'next/error'
import MetaSeo from 'components/atomic/MetaSeo'
import { useRouter } from 'next/router'
import { api as pagebuilderApi } from 'react-pagebuilder/services/api'
import { useDispatch } from 'react-redux'
import { setItem } from 'containers/toolbar'
import { Button, NewTags, TabGroup } from 'react-components/atoms'
import { useTrans } from 'hooks'
import { Bar, StickyActions } from 'react-components/organisms'
import Credits from 'components/atomic/Credits'
import { ArrowDown } from 'react-components/atoms/Icons'
import { BoxAttivaLibro, CardParascolastico } from 'react-components/molecules'
import orderBy from 'lodash/orderBy'
import PlaceholderReact from 'components/atomic/PlaceholderReact'
import { sendProductImpressionGTM, sendProductDetailGTM } from '../../utils/gtm'

const ParascolasticoDetail = ({ item, page, newDefaultContent, statusCode }) => {
  if (!item) return <Error statusCode={statusCode} />

  const t = useTrans()

  const dispatch = useDispatch()

  const router = useRouter()

  const [parascolastico, setParascolastico] = useState(item)
  const [volumi, setVolumi] = useState([])

  const [anchor, setAnchor] = useState('')

  useEffect(() => {
    if (volumi) {
      sendProductImpressionGTM(volumi, 'prodotti_correlati')
    }
  }, [volumi])

  const refresh = async () => {
    const { data } = await api.get(`${PARASCOLASTICO}${parascolastico.id}/`)
    setParascolastico(data)
    sendProductDetailGTM(data, 'navigazione_standard')
    dispatch(setItem(data))
  }

  useEffect(() => {
    refresh()
    initVolumi()
  }, [])

  useEffect(() => {
    if (router.asPath) initAnchor()
  }, [router.asPath])

  function initAnchor() {
    const anchor = router.asPath.split('#')[1]
    if (anchor) setAnchor(anchor)
  }

  async function initVolumi() {
    const params = { parascolastico: parascolastico.id }
    const { data } = await api.get(VOLUME, { params })
    setVolumi(data)
  }

  // GESTIONE TAB
  // ho materie -> N tab con nome materia e i volumi associati + 1 con nome "Volume unico" con gli altri volumi
  // non no materie -> 1 tab con nome "Il volume"

  function getTabs(items) {
    if (!items.length) return []

    let mapping = {}

    const senzaMaterie = !items.some((i) => getMateria(i.tassonomia))

    if (senzaMaterie) {
      const tabLabel = items.length > 1 ? 'I volumi' : 'Il volume'
      mapping[tabLabel] = items // 1 tab con nome "I volumi" o "Il volume"
    } else {
      mapping = { 'Volume unico': [] }

      items.forEach((i) => {
        const materie = getDiscipline(i.tassonomia)
        if (materie.length > 1 || !materie.length) {
          mapping['Volume unico'].push(i) // 1 con nome "Volume unico" con gli altri volumi
        } else {
          if (materie[0] in mapping) {
            mapping[materie[0]].push(i) // N tab con nome materia e i volumi associati
          } else {
            mapping[materie[0]] = [i]
          }
        }
      })
    }

    return orderBy(
      Object.entries(mapping).map(([key, value]) => ({
        key: slugify(key),
        label: key,
        items: value,
      })),
      ['label']
    )
  }

  const tabs = useMemo(
    () =>
      getTabs(volumi).map(({ key, label, items }) => ({
        key,
        label,
        content: (
          <div className="catalogo-page__parascolastici">
            {items.map((item, index) => (
              <CardParascolastico
                className="catalogo-page__parascolastici__card"
                parascolastico={item}
                key={item.id}
                position={index + 1}
                parentList={'prodotti_correlati'}
              />
            ))}
          </div>
        ),
      })),
    [volumi]
  )

  // TAB CON VECCHIO CONTENUTO (DA TENERE)
  const oldTabs = useMemo(
    () =>
      parascolastico.tabs.map((tab, index) => ({
        key: tab.slug,
        label: tab.titolo,
        content: (
          <PlaceholderReact
            contentTypeId={parascolastico.model}
            objectId={parascolastico.id}
            pkey={`${parascolastico.id}-${tab.slug}-content`}
            serverSideContent={index === 0 ? newDefaultContent : null}
          />
        ),
      })),
    [parascolastico]
  )

  const finalTabs = volumi.length ? tabs.concat(oldTabs) : oldTabs

  return (
    <main className={`catalogo-page ${!tabs.length ? 'catalogo-page--no-tabs' : ''}`}>
      <MetaSeo seo={page.seo} />
      <div className="w-container">
        <Breadcrumbs breadcrumbs={page.breadcrumbs} />
      </div>
      <div className="w-container">
        <div className="catalogo-page__sticky-container">
          <StickyActions item={parascolastico} labelGTM={'parascolastico'} share />
        </div>
        <div className="catalogo-page__header-container">
          <div className="catalogo-page__header">
            {parascolastico.main_image && (
              <div className="catalogo-page__image-box">
                <Image
                  src={CAS_DOMAIN + parascolastico.main_image}
                  alt={parascolastico.titolo.toAltTitle()}
                  title={parascolastico.titolo.toAltTitle()}
                  width={285}
                  height={360}
                  priority
                />
              </div>
            )}
            <div className="catalogo-page__summary">
              <h1 className="catalogo-page__title">{parascolastico.titolo}</h1>
              <NewTags
                tassonomia={parascolastico.tassonomia}
                discipline
                className="catalogo-page__tags"
              />
              <p className="catalogo-page__abstract">{parascolastico.descrizione}</p>
              <div className="catalogo-page__editore">
                Editore <b>{parascolastico.editore || t('SOC. GIUNTI SCUOLA')}</b>
              </div>
              <BoxAttivaLibro
                libro={parascolastico}
                className="catalogo-page__box-attiva-libro desktop"
              />
            </div>
          </div>
        </div>
      </div>
      {finalTabs.length ? (
        <div className="catalogo-page__tabs">
          <div className="w-container">
            <TabGroup
              tabs={finalTabs}
              initialKey={anchor || finalTabs[0].key}
              className="catalogo-page__tabgroup"
              variant="white"
            />
            {finalTabs.map((tab, index) => (
              <Credits
                label={tab.label}
                crediti={tab.content}
                classes="catalogo-page__credits"
                variant="catalogo"
                icon={<ArrowDown />}
                initialOpen={anchor === tab.key || (!anchor && index === 0)}
                href={`#${tab.key}`}
                key={tab.slug}
              />
            ))}
          </div>
        </div>
      ) : null}

      <div className="w-container">
        <BoxAttivaLibro libro={parascolastico} className="catalogo-page__box-attiva-libro mobile" />
      </div>

      <Bar item={parascolastico} share labelGTM={'parascolastico'}>
        {parascolastico.box_attiva_libro && (
          <Button label={t('Attiva libro')} href="/area-personale/libreria" />
        )}
      </Bar>
    </main>
  )
}

export async function getStaticPaths() {
  const response = await api.get(PARASCOLASTICO)
  const parascolastici = response.data

  const paths = parascolastici
    .map((s) => {
      return { params: { slug: s.slug } }
    })
    .slice(0, 1)

  return {
    paths,
    fallback: 'blocking',
  }
}

export const getStaticProps = async ({ params: { slug }, preview, previewData }) => {
  const headers = preview && previewData?.headers ? { cookie: previewData.headers } : {}

  const response = await api.get(
    `${PARASCOLASTICO}${SLUG}${slug}/`,
    { params: { preview } },
    { headers }
  )
  const item = response.data

  if (!item) {
    return {
      notFound: true,
    }
  }

  let newDefaultContent = null

  if (item.tabs.length) {
    newDefaultContent = await pagebuilderApi.getPagebuilder({
      contentTypeId: item.model,
      objectId: item.id,
      key: item.id + '-' + item.tabs[0].slug + '-content',
      edit_mode: false,
    })
  }

  const page = item.pagina

  const toolbarProps = { toolbarItem: item, toolbarPage: page }

  return {
    props: {
      item,
      page,
      newDefaultContent,
      key: item.id,
      ...toolbarProps,
      revalidate: 60 * 10, // trovare alternativa in stile data-fetching-wrapper di Luca D. per altre pagine senza dover passare manualmente il revalidate
    },
    revalidate: 60 * 10,
  }
}

export default ParascolasticoDetail
